import React, { useState, useEffect } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { useAuthStore } from '../../store';
import { useIsMobile } from '../../hooks';

export interface AdminBreadcrumb {
  label: string;
  to?: string;
}

interface AdminLayoutProps {
  title: string;
  breadcrumbs?: AdminBreadcrumb[];
  actions?: React.ReactNode;
  children: React.ReactNode;
}

const navItems = [
  { to: '/admin', label: 'Dashboard' },
  { to: '/admin/users', label: 'Users' },
  { to: '/admin/worlds', label: 'Worlds' },
  { to: '/admin/invitations', label: 'Invitations' },
  { to: '/admin/usage', label: 'Usage' },
  { to: '/admin/model-stats', label: 'Model Stats' },
  { to: '/admin/inference', label: 'Inference' },
  { to: '/admin/scaffolding', label: 'Scaffolding' },
  { to: '/admin/eval', label: 'Eval' },
];

export function AdminLayout({ title, breadcrumbs, actions, children }: AdminLayoutProps) {
  const location = useLocation();
  const user = useAuthStore(state => state.user);
  const isMobile = useIsMobile();
  const [drawerOpen, setDrawerOpen] = useState(false);

  // Lock body scroll when drawer is open
  useEffect(() => {
    if (isMobile && drawerOpen) {
      document.body.style.overflow = 'hidden';
      return () => { document.body.style.overflow = ''; };
    }
  }, [isMobile, drawerOpen]);

  const sidebarContent = (
    <aside className="w-60 bg-dark-800 border-r border-dark-700 flex flex-col h-full">
      <div className="px-5 py-4 border-b border-dark-700">
        <Link to="/" className="text-sm text-dark-300 hover:text-dark-50" onClick={() => setDrawerOpen(false)}>
          &larr; Back to App
        </Link>
        <div className="mt-3 text-lg font-semibold text-dark-50">Admin</div>
        <div className="text-xs text-dark-400 truncate">{user?.email}</div>
      </div>
      <nav className="flex-1 px-3 py-4 space-y-1">
        {navItems.map(item => (
          <NavLink
            key={item.to}
            to={item.to}
            onClick={() => setDrawerOpen(false)}
            className={({ isActive }) => {
              const isRoot = item.to === '/admin';
              const pathname = location.pathname.replace(/\/+$/, '');
              const target = item.to.replace(/\/+$/, '');
              const active = isRoot
                ? pathname === target
                : isActive || pathname.startsWith(target + '/');
              return active
                ? 'block px-3 py-2 rounded-md bg-primary-600/20 text-primary-200'
                : 'block px-3 py-2 rounded-md text-dark-300 hover:text-dark-50 hover:bg-dark-700';
            }}
            end={item.to === '/admin'}
          >
            {item.label}
          </NavLink>
        ))}
      </nav>
    </aside>
  );

  return (
    <div className="min-h-screen bg-dark-900 flex">
      {/* Desktop sidebar */}
      {!isMobile && sidebarContent}

      {/* Mobile drawer */}
      {isMobile && (
        <>
          {drawerOpen && (
            <div
              className="fixed inset-0 bg-black/50 z-40"
              onClick={() => setDrawerOpen(false)}
            />
          )}
          <div
            className={`fixed inset-y-0 left-0 z-50 transform transition-transform duration-300 ease-in-out ${
              drawerOpen ? 'translate-x-0' : '-translate-x-full'
            }`}
          >
            {sidebarContent}
          </div>
        </>
      )}

      <div className="flex-1 flex flex-col min-w-0">
        <header className="border-b border-dark-700 bg-dark-800 px-4 md:px-6 py-4">
          <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
            <div className="flex items-center gap-3">
              {isMobile && (
                <button
                  onClick={() => setDrawerOpen(true)}
                  className="text-dark-200 hover:text-dark-50 p-1"
                  aria-label="Open admin menu"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                  </svg>
                </button>
              )}
              <div>
                {breadcrumbs && breadcrumbs.length > 0 && (
                  <div className="text-sm text-dark-400 flex items-center gap-2 flex-wrap">
                    {breadcrumbs.map((crumb, index) => (
                      <span key={`${crumb.label}-${index}`} className="flex items-center gap-2">
                        {crumb.to ? (
                          <Link to={crumb.to} className="hover:text-dark-50">
                            {crumb.label}
                          </Link>
                        ) : (
                          <span className="text-dark-300">{crumb.label}</span>
                        )}
                        {index < breadcrumbs.length - 1 && <span className="text-dark-600">/</span>}
                      </span>
                    ))}
                  </div>
                )}
                <h1 className="text-2xl font-semibold text-dark-50">{title}</h1>
              </div>
            </div>
            {actions && <div className="flex items-center gap-3">{actions}</div>}
          </div>
        </header>

        <main className="flex-1 p-4 md:p-6">{children}</main>
      </div>
    </div>
  );
}
